import { useMemo } from 'react';
import { DEFAULT_LAYOUT_ID } from '../components/AppLayout/Layouts/tokens';
import { getNavGroupSlug, getSpaceBasePath } from '../components/NavigationSidebar/spacesNavPath';
import { useAppLayoutConfig } from '../providers/AppLayoutConfig/AppLayoutConfigContext';
import type { LayoutSection } from '../types/LayoutConfig';
import { toNavGroupName } from './useSpacesNavItems';

/**
 * Shared routing helpers for sections that use dynamic user-defined spaces
 * (Trading, Monitoring, Intel, Dealer). Provides:
 *
 * - `navGroupNames` — distinct URL slugs for every `LayoutSpec.group` in the section’s `layouts`,
 *   used to register `/:navGroupName/:layoutId/*` routes
 *   before the plain `/:layoutId/*` catch-all. Derived from space config only
 *   (no `layoutId` argument needed for this list).
 *
 * - `indexRedirectPath` — where a section's Spaces index should send the user:
 *   one concrete space. Requires the **canonical** layout id (last-used / device
 *   choice); you cannot build this from “all layouts” because the index must
 *   pick exactly one target.
 */
export function useSpacesRouting(layoutSection: LayoutSection, canonicalLayoutId: string | undefined) {
  const { layoutConfigs } = useAppLayoutConfig(layoutSection);

  const navGroupNames = useMemo(() => {
    const slugs = new Set<string>();
    for (const l of layoutConfigs ?? []) {
      const g = l.group;
      if (g != null && g !== '') {
        slugs.add(toNavGroupName(g));
      }
    }
    return [...slugs];
  }, [layoutConfigs]);

  const layoutIds = useMemo(() => new Set((layoutConfigs ?? []).map(layout => layout.id)), [layoutConfigs]);

  const indexRedirectPath = useMemo(() => {
    const layoutId = canonicalLayoutId ?? DEFAULT_LAYOUT_ID;
    const navGroupSlug = getNavGroupSlug(layoutConfigs, layoutId);
    return getSpaceBasePath(layoutSection, navGroupSlug, layoutId);
  }, [canonicalLayoutId, layoutSection, layoutConfigs]);

  return { navGroupNames, layoutIds, indexRedirectPath };
}
